import { useEffect, useRef, useState } from 'react';
import { MS_MINIMOS_POR_GESTO, mirar, type Gesto } from '@validate/core';
import { fotogramaEnGrises } from './camara.js';

/**
 * La detección de gestos: lo que el navegador AFIRMA haber visto.
 *
 * ── LEER ESTO ANTES DE CREERSE NADA ────────────────────────────────────────────
 *
 * Todo lo que sale de aquí viaja al servidor como `afirmadoPorElCliente` — y el core es
 * explícito en que eso no es un hecho nuestro (`prueba-de-vida.ts`). Este módulo existe para
 * que la PERSONA honesta pueda completar la coreografía con naturalidad; contra un navegador
 * modificado no defiende nada, y no pretende hacerlo. La defensa real son los píxeles en el
 * servidor (#79).
 *
 * ── EL GIRO SE MIDE CON LA NARIZ, NO CON LA MATRIZ DE POSE ─────────────────────
 *
 * El FaceLandmarker puede devolver la matriz de transformación de la cabeza, y de ahí sale
 * un yaw "de verdad". No se usa, y no es pereza: extraer un ángulo de Euler de una matriz
 * 4×4 column-major tiene exactamente el tipo de error de eje/signo que compila perfecto y
 * gira al revés. La nariz dentro de la caja de la cara es más tosca y no se puede equivocar
 * de eje: si la persona gira a SU izquierda, su nariz se acerca al borde derecho de la
 * imagen (la cámara la ve especular). Punto.
 */

/** La nariz. Índice 1 de la malla de 478 puntos de MediaPipe. */
const INDICE_DE_LA_NARIZ = 1;

/**
 * Dónde cae la nariz (0 = borde izquierdo de la cara en la imagen, 1 = derecho) para dar
 * cada giro por hecho. Centrado es ~0.5.
 *
 * Son deliberadamente exigentes a medias: un giro de verdad lleva la nariz más allá de 0.66
 * sin esfuerzo, pero pedir 0.8 obligaría a girar hasta perder la cara del encuadre — y ahí
 * el detector suelta el rastro y el gesto no se confirma nunca.
 */
const NARIZ_CENTRADA = { min: 0.36, max: 0.64 };
const NARIZ_GIRADA_A_SU_IZQUIERDA = 0.66; // en la imagen: hacia la derecha
const NARIZ_GIRADA_A_SU_DERECHA = 0.34;

/** Umbrales de blendshape. Neutro ronda 0.05; el gesto franco pasa de 0.6. */
const UMBRAL_SONRISA = 0.4;
const UMBRAL_PARPADEO = 0.45;

/**
 * Cuánto tiene que sostenerse un gesto antes de confirmarse.
 *
 * Dos motivos, y el segundo importa más:
 *
 *   1. Un solo fotograma es ruido. Tres seguidos (~270 ms) es una intención.
 *   2. El core marca 'sospechosa' cualquier confirmación por debajo de `MS_MINIMOS_POR_GESTO`
 *      (250 ms). Una persona que YA está centrada cuando se le pide "céntrate" cumpliría en
 *      el primer fotograma — y nuestra propia pantalla la convertiría en sospechosa. Por eso
 *      nada se confirma antes del mínimo del core más un margen.
 *
 * El parpadeo es la excepción a los tres fotogramas: dura ~150 ms entero y a ~11 fps se ve
 * en UN fotograma con suerte. Exigirle racha sería pedirle a la persona que aguante los ojos
 * cerrados — que ya no es parpadear.
 */
const FOTOGRAMAS_SEGUIDOS = 3;
const MS_MINIMOS_ANTES_DE_CONFIRMAR = MS_MINIMOS_POR_GESTO + 450;

/** Cada cuánto se mira. ~11 fps: suficiente para gestos, soportable para un móvil viejo. */
const MS_ENTRE_DETECCIONES = 90;

/** Instrucciones para la persona. El estilo del Figma: imperativo, corto, una línea. */
export const INSTRUCCION: Record<Gesto, string> = {
  frente: 'Centra tu rostro en el marco.',
  izquierda: 'Gira la cabeza lentamente a la izquierda.',
  derecha: 'Gira la cabeza lentamente a la derecha.',
  sonreir: 'Sonríe a la cámara.',
  parpadear: 'Parpadea un par de veces.',
};

interface Punto {
  x: number;
  y: number;
}
interface Blendshape {
  categoryName: string;
  score: number;
}
interface Landmarker {
  detectForVideo(
    video: HTMLVideoElement,
    ms: number,
  ): {
    faceLandmarks: Punto[][];
    faceBlendshapes: { categories: Blendshape[] }[];
  };
}

/** Carga el FaceLandmarker una sola vez. Mismo patrón y mismos motivos que `deteccion.ts`. */
let promesaDelLandmarker: Promise<Landmarker | null> | null = null;

async function cargarLandmarker(): Promise<Landmarker | null> {
  promesaDelLandmarker ??= (async () => {
    try {
      const vision = await import('@mediapipe/tasks-vision');
      const fileset = await vision.FilesetResolver.forVisionTasks('/mediapipe/wasm');
      const d = await vision.FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: '/models/face_landmarker.task', delegate: 'GPU' },
        runningMode: 'VIDEO',
        numFaces: 1,
        outputFaceBlendshapes: true,
      });
      return d as unknown as Landmarker;
    } catch {
      // Sin motor no hay gestos. La pantalla se entera por `motorCargado` y le da a la
      // persona una salida — no un silencio.
      return null;
    }
  })();
  return promesaDelLandmarker;
}

export interface EstadoDeGesto {
  motorCargado: boolean;
  hayRostro: boolean;
  /**
   * Dónde está la cara respecto al ENCUADRE. La pantalla lo traduce a instrucciones para
   * la persona; aquí solo se dice qué pasa, no qué hacer.
   */
  colocacion: 'ok' | 'sin_rostro' | 'sube' | 'baja' | 'centra' | 'acerca' | 'aleja';
  luzOk: boolean;
  nitidezOk: boolean;
}

/**
 * Mira el vídeo y llama a `onDetectado(ms)` UNA vez cuando el gesto pedido se sostiene.
 *
 * `ms` es lo que tardó desde que el gesto se pidió — es el dato que el servidor compara
 * contra `MS_MINIMOS_POR_GESTO`. Se mide aquí y no en la pantalla para que nadie lo mida dos
 * veces de dos formas.
 */
export function useDeteccionDeGesto(
  video: HTMLVideoElement | null,
  gesto: Gesto,
  activo: boolean,
  onDetectado: (ms: number) => void,
): EstadoDeGesto {
  const [estado, setEstado] = useState<EstadoDeGesto>({
    motorCargado: false,
    hayRostro: false,
    colocacion: 'sin_rostro',
    luzOk: true,
    nitidezOk: true,
  });

  // La función de la pantalla cambia en cada render; el lazo no debe reiniciarse por eso.
  const alDetectar = useRef(onDetectado);
  alDetectar.current = onDetectado;

  useEffect(() => {
    if (video === null || !activo) return;

    let vivo = true;
    let detector: Landmarker | null = null;
    let seguidos = 0;
    let disparado = false;
    let ultimaDeteccion = 0;
    let ultimaCalidad = 0;
    const inicio = performance.now();
    const lienzo = document.createElement('canvas');

    void cargarLandmarker().then((d) => {
      if (!vivo) return;
      detector = d;
      setEstado((e) => ({ ...e, motorCargado: d !== null }));
    });

    function cumple(lm: Punto[], blend: readonly Blendshape[]): boolean {
      const score = (nombre: string): number =>
        blend.find((b) => b.categoryName === nombre)?.score ?? 0;

      const nariz = lm[INDICE_DE_LA_NARIZ];
      if (nariz === undefined) return false;

      let minX = Infinity;
      let maxX = -Infinity;
      for (const p of lm) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
      }
      if (maxX - minX < 0.01) return false;
      const pos = (nariz.x - minX) / (maxX - minX);

      switch (gesto) {
        case 'frente':
          return pos >= NARIZ_CENTRADA.min && pos <= NARIZ_CENTRADA.max;
        case 'izquierda':
          return pos >= NARIZ_GIRADA_A_SU_IZQUIERDA;
        case 'derecha':
          return pos <= NARIZ_GIRADA_A_SU_DERECHA;
        case 'sonreir':
          return (score('mouthSmileLeft') + score('mouthSmileRight')) / 2 >= UMBRAL_SONRISA;
        case 'parpadear':
          return (score('eyeBlinkLeft') + score('eyeBlinkRight')) / 2 >= UMBRAL_PARPADEO;
      }
    }

    /**
     * La colocación: dónde cae la caja de la cara dentro del encuadre (coordenadas
     * normalizadas 0-1 de MediaPipe). Probado en teléfono: la cara salía pegada al borde
     * inferior y el selfie luego no cotejaba — esto es lo que permite avisar A TIEMPO.
     *
     * Ojo con la dirección de 'sube'/'baja': son instrucciones para la PERSONA sobre el
     * teléfono, no descripciones de la imagen. La cámara frontal no espeja la coordenada
     * vertical: una cara que sale abajo (centroY alto — la Y crece hacia abajo) se arregla
     * SUBIENDO el teléfono. El texto exacto lo pone la pantalla.
     */
    function colocacionDe(lm: Punto[] | undefined): EstadoDeGesto['colocacion'] {
      if (lm === undefined || lm.length === 0) return 'sin_rostro';
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      for (const p of lm) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
      const centroX = (minX + maxX) / 2;
      const centroY = (minY + maxY) / 2;
      const ancho = maxX - minX;
      // El orden importa: primero lo vertical (la queja real del campo), luego lo
      // horizontal, luego la distancia. Corregir de una cosa en una.
      if (centroY > 0.62) return 'sube';
      if (centroY < 0.3) return 'baja';
      if (centroX < 0.35 || centroX > 0.65) return 'centra';
      if (ancho < 0.18) return 'acerca';
      if (ancho > 0.62) return 'aleja';
      return 'ok';
    }

    let rafId = 0;
    const paso = (): void => {
      if (!vivo) return;
      rafId = requestAnimationFrame(paso);

      const ahora = performance.now();
      if (ahora - ultimaDeteccion < MS_ENTRE_DETECCIONES) return;
      ultimaDeteccion = ahora;

      // OJO: `disparado` ya no corta aquí. Antes sí, y eso dejaba a la fase de grabación
      // a ciegas: tras confirmar 'frente' el estado se congelaba y la pantalla no podía
      // saber si la cara seguía bien puesta mientras la ráfaga juntaba señal. Ahora el
      // lazo sigue calculando y publicando (hayRostro, colocación, luz, nitidez) y lo
      // único que se omite tras disparar es volver a confirmar gestos — más abajo.
      if (detector === null || video.videoWidth === 0) return;

      let lm: Punto[] | undefined;
      let blend: Blendshape[] = [];
      try {
        const r = detector.detectForVideo(video, ahora);
        lm = r.faceLandmarks[0];
        blend = r.faceBlendshapes[0]?.categories ?? [];
      } catch {
        return; // un fotograma que el detector no digiere no tumba el lazo
      }

      const hayRostro = lm !== undefined && lm.length > 0;
      const colocacion = colocacionDe(lm);

      // Las pastillas de Luz/Nitidez, cada ~4 detecciones. El juez es el mismo `mirar` del
      // core que usa la captura — dos jueces de calidad divergirían tarde o temprano.
      if (ahora - ultimaCalidad > 350) {
        ultimaCalidad = ahora;
        const g = fotogramaEnGrises(video, lienzo);
        if (g !== null) {
          const m = mirar({ ...g, hayRostro, anchoDelRostro: hayRostro ? 0.5 : 0 }, 'rostro');
          setEstado((e) =>
            e.hayRostro === hayRostro &&
            e.colocacion === colocacion &&
            e.luzOk === (m.calidad.luz === 'ok') &&
            e.nitidezOk === (m.calidad.nitidez === 'ok')
              ? e
              : {
                  ...e,
                  hayRostro,
                  colocacion,
                  luzOk: m.calidad.luz === 'ok',
                  nitidezOk: m.calidad.nitidez === 'ok',
                },
          );
        }
      }

      // Lo único que `disparado` apaga: la confirmación. El estado de arriba sigue vivo.
      if (disparado) return;

      if (lm === undefined || !cumple(lm, blend)) {
        seguidos = 0;
        return;
      }

      // La CONFIRMACIÓN de 'frente' exige además colocación 'ok': de ese gesto salen el
      // selfie y la ráfaga, y una cara pegada al borde da un selfie cortado que después no
      // coteja con el documento. A los demás gestos NO se les exige — girar la cabeza
      // mueve el centro de la caja y sería pedir lo imposible.
      if (gesto === 'frente' && colocacion !== 'ok') {
        seguidos = 0;
        return;
      }

      seguidos++;
      const suficientes = gesto === 'parpadear' ? 1 : FOTOGRAMAS_SEGUIDOS;
      const transcurrido = ahora - inicio;

      if (seguidos >= suficientes && transcurrido >= MS_MINIMOS_ANTES_DE_CONFIRMAR) {
        disparado = true;
        alDetectar.current(Math.round(transcurrido));
      }
    };
    rafId = requestAnimationFrame(paso);

    return () => {
      vivo = false;
      cancelAnimationFrame(rafId);
    };
  }, [video, gesto, activo]);

  return estado;
}
